import { Component, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import * as moment from 'moment';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  Subject,
  takeUntil
} from 'rxjs';
import { ModalService } from 'src/app/_services/modal.service';
import { ProfileService } from 'src/app/_services/profile.service';
import { TimesheetService } from 'src/app/_services/timesheet.service';
import { UtilityService } from 'src/app/_services/utility.service';
import { DayColumn } from 'src/app/models/day-column.model';
import { BaseComponent } from '../components/base/base.component';
import { ModalNoteTimesheetComponent } from './modal-note/modal-note-timesheet.component';
import { ModalRequestTimesheetComponent } from './modal-request-timesheet/modal-request-timesheet.component';

@Component({
  selector: 'app-new-timesheet',
  templateUrl: './new-timesheet.component.html',
  styleUrls: ['./new-timesheet.component.css']
})
export class NewTimesheetComponent extends BaseComponent {
  private readonly _timesheetService: TimesheetService =
    inject(TimesheetService);
  private readonly _title: Title = inject(Title);
  public readonly _utilityService: UtilityService = inject(UtilityService);
  private readonly _profileService: ProfileService = inject(ProfileService);
  private readonly _NzmodalService: NzModalService = inject(NzModalService);
  private readonly _modalService: ModalService = inject(ModalService);

  search = new FormControl('');
  selectedDate: Date = new Date();
  selectedWeek: { start: string; end: string } | null = { start: '', end: '' };
  listOfColumns: DayColumn[] = [];

  dateRender: Date | null = null;
  highlightedDatesError: string[] = [];
  highlightedDatesRequest: string[] = [];
  usersSelect: any[] = [];
  projects: any[] = [];
  previousTime: string | null = null;
  timePickerOpen: boolean = false;
  indexProject: number;
  indexDay: number;
  chosenUser?: string;
  totaliTimesheet: any[] = [];
  profileData: any;
  userRole: string;
  selectedUser: any;
  currentDate: string = moment(new Date()).format('YYYY-MM-DD');
  indexUserSelected: number;
  incorrectDays: string[] = [];
  isScrum: boolean;
  blockedDateSet = new Set<string>();

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor() {
    super();
  }

  async ngOnInit() {
    this.profileData = await this._profileService.getUserRoleAsync();
    this.userRole = this.profileData.roles[0].name;

    // flag Scrum Master
    const assignmentRoles =
      this.profileData?.assignmentroles ??
      this.profileData?.assignment_roles ??
      [];
    this.isScrum = assignmentRoles.some((role: any) => {
      const roleId = role?.pivot?.assignmentrole_id ?? role?.id;
      const roleAlias = role?.alias ?? role;
      return roleId === 12 || roleAlias === 'Scrum Master';
    });

    this._title.setTitle('Timesheet | Sviluppo personale');
    this._utilityService.setTitle('Timesheet');
    // se arriviamo da una notifica, prendiamo i dati salvati nel servizio
    if (
      this._timesheetService.date != null &&
      this._timesheetService.chosenUser != null
    ) {
      // aspettiamo il recupero degli utenti
      this.usersSelect = await firstValueFrom(
        this._timesheetService.getUsersTimesheet('')
      );
      this.setSelectedWeekFromDate(this._timesheetService.date);

      // recuperiamo l'utente salvato sul service di timesheet
      this.chosenUser = this._timesheetService.chosenUser.id;

      this.selectedUser = this.usersSelect.find((user: any, index: number) => {
        if (user.id === this.chosenUser) {
          // salviamo l'indice per selezionare l'utente nella lista degli utenti
          this.indexUserSelected = index;

          this.changeUserSelected(user, this.indexUserSelected);
          return true;
        }
        return false;
      });

      // passiamo true alla funzione getTimesheet per indicare che siamo arrivati da una notifica e se abbiamo la possibilità
      // di visualizzare i ltimesheet di altri utenti
      if (this.selectedUser) this.getTimesheet(true);
      else this.getTimesheet(false);
      this._timesheetService.date = null;
      this._timesheetService.chosenUser = null;
    } else {
      this.getTimesheetUsers();
      this.getTimesheet();
    }
    this.search.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        this.getTimesheetUsers(value!);
      });
  }

  /**
   * Funzione che serve per chiamare la index dei timesheet
   * @param isNotification booleano che indica se siamo arrivati da una notifiche
   */
  getTimesheet(isNotification: boolean = false) {
    this._timesheetService
      .getTimesheet(
        this.chosenUser ?? this.profileData.id,
        this.selectedWeek?.start,
        'value'
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          // 1) costruisco il set con le date bloccate
          const blockedDateSet = new Set<string>(
            (res.listOfColumns as DayColumn[])
              .filter(
                d =>
                  d.isHolidayDay ||
                  d.isSicknessDay ||
                  (d.is_part_time_day && !d.is_working_day)
              )
              .map(d => d.date)
          );

          this.blockedDateSet = blockedDateSet;

          // 2) converto i valori e aggiungo il flag isBlocked ai days dei progetti
          res.projects.forEach((project: any) => {
            project.days.forEach((day: any) => {
              day.billing_value = this.convertToTimeObject(day.billing_value);
              day.value = this.convertToTimeObject(day.value);

              day.isBlocked =
                blockedDateSet.has(day.date) ||
                (day.is_part_time_day && !day.is_working_day);
            });
          });

          this.listOfColumns = res.listOfColumns;
          this.projects = res.projects;
          this.totaliTimesheet = res.totals;
          this.selectedUser = res.authUser;
          this.incorrectDays = res.authUser.incorrect_days ?? [];

          // Se l’utente non è ancora selezionato, impostalo
          if (!this.selectedUser && !isNotification) {
            this.selectedUser = res.authUser;
            this.indexUserSelected = 0;
            this.chosenUser = this.selectedUser.id;
          }

          // adiamo ad impostare le date evidenziate per gli errori e le richieste
          this.setHighlightedErrorDates(
            res.authUser.calendar_incorrect_days ??
              res.authUser.incorrect_days ??
              []
          );
          this.setHighlightedRequestDates(res.authUser.requests ?? []);
        }
      });
  }

  /**
   * Questa funzione serve per recuperare gli utenti che possono essere selezionati nel timesheet
   * e per filtrare in base alla ricerca effettuata
   * @param search ricerca da effettuare sugli utenti
   */
  async getTimesheetUsers(search: string = '') {
    const res = await firstValueFrom(
      this._timesheetService.getUsersTimesheet(this.search.value! ?? search)
    );
    this.usersSelect = res;

    // segnaliamo sempre quale utente è selezionato anche durante la ricerca
    this.indexUserSelected = this.usersSelect.findIndex(
      (user: any) => user.id === this.chosenUser
    );
  }

  /**
   * Funzione che serve per ricavare il lunedì della settimana corrente e impostarlo nel datepicker per filtrare la tabella
   */
  setDefaultDate(): void {
    const currentMonday = moment().startOf('isoWeek'); // Trova il lunedì della settimana corrente
    this.selectedDate = currentMonday.toDate(); // Imposta il date-picker con il lunedì corrente

    // Calcola l'intervallo della settimana (dal lunedì al venerdì)
    this.selectedWeek = {
      start: currentMonday.format('YYYY-MM-DD'),
      end: currentMonday.isoWeekday(5).format('YYYY-MM-DD')
    };
  }

  /**
   * Funzione che serve per inserire/cambiare le ore nel time-picker
   * @param event nuova tempo inserita
   */
  changeTime(
    time: Date,
    projectId: number,
    userId: string,
    date: Date,
    type: string,
    i: number,
    j: number
  ) {
    this.indexProject = i;
    this.indexDay = j;
    if (this.timePickerOpen) {
      if (moment(time).format('HH:mm') != this.previousTime) {
        this._timesheetService
          .saveTimeSheet(
            moment(time).format('HH:mm'),
            projectId,
            userId ?? this.profileData.id,
            moment(date).format('YYYY-MM-DD'),
            type
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              this.getTimesheet();
              this.getTimesheetUsers();
              this.responseToast('success', res.message);
              if (this.selectedUser) {
                this.selectedUser = this.usersSelect.find((user: any) => {
                  return user.id === this.chosenUser;
                });
              }
            },
            error: (err: any) => {
              this.responseToast('error', err.error.error);
            }
          });
      }
    }
  }

  /**
   * Funzione che serve per disabilitare le ore
   * @returns array: number
   */
  disabledHours(): number[] {
    return [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];
  }

  /**
   * funzione che serve per disabilitare i minuti in base alle ore selezionate
   * @param hour ora selezionata
   * @returns array
   */
  disabledMinutes(hour: number): number[] {
    if (hour == 8) {
      return Array.from({ length: 60 }, (_, i) => i + 1);
    }
    return [];
  }

  /**
   * questa funzione parte quando cambiamo un'ora nel time picker
   * @param open controlliamo se il pannello per selezionare le ore è aperto
   * @param time ora precedentemente selezionata di scrum o fatturazione
   */
  onTimePickerOpenChange(open: boolean, time: any) {
    if (open) {
      this.previousTime = moment(time).format('HH:mm'); // Salva il valore precedente quando il time picker si apre
    }
    this.timePickerOpen = open;
  }

  /**
   * Funzione che serve a recuperare tutta l'intera settimana selezionata
   * @param date data di inzio settimana
   * @returns void
   */
  onDateChange(date: Date): void {
    if (!date) {
      this.selectedWeek = null;
      return;
    }
    // andiamo a salvare l'inizio settimana e il fine settimana (venerdì)
    const startOfWeek = moment(date).startOf('isoWeek');
    const endOfWeek = moment(date).isoWeekday(5);

    this.selectedWeek = {
      start: startOfWeek.format('YYYY-MM-DD'),
      end: endOfWeek.format('YYYY-MM-DD')
    };
    this.selectedDate = startOfWeek.toDate(); // primo giorno della settimana
    this.getTimesheet();
  }

  /**
   * questa funzione serve per convertire una stringa (tempo) in una data
   * @param timeString stringa (tempo) da convertire in data
   * @returns ritorna un tipo data
   */
  convertToTimeObject(timeString: string): Date | null {
    if (
      !timeString ||
      typeof timeString !== 'string' ||
      !timeString.includes(':')
    )
      return null; // Gestione del valore nullo

    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0); // Imposta solo ore e minuti
    return date;
  }
  /**
   * Funzione che serve per assegnare il background ad una cella dei totali
   */
  getBackgroundCellHours(date: string): string {
    // Escludo il giorno odierno: nessun colore
    if (date === this.currentDate) {
      return 'none';
    }
    // Giorni futuri → nessun colore
    if (date > this.currentDate) return 'none';

    // Se la data è nella lista degli errori → rosso chiaro
    if (this.incorrectDays?.includes(date)) {
      return 'var(--Red-50)';
    }

    // Se il giorno è stato ignorato (es. PT 30h) → verde chiaro
    const ignoredDay = this.totaliTimesheet.find(
      d => d.date === date
    )?.is_ignored;
    if (ignoredDay) {
      return 'var(--Green-50)';
    }

    //  Altrimenti giorno corretto → verde chiaro
    return 'var(--Green-50)';
  }

  /**
   * Dice se per quella giornata part-time l'utente può usare l'azione "ignora".
   *
   * Regola:
   * - se il giorno è part-time, applichiamo la logica nuova day-by-day
   * - se il giorno non è part-time, manteniamo il comportamento legacy del
   *   Timesheet basato sulla expectedCapacity utente
   */
  canIgnoreIncorrectDay(column: DayColumn): boolean {
    if (column.is_part_time_day) {
      return !!column.is_working_day;
    }

    return this.getLegacyIgnoreDayAvailability();
  }

  /**
   * Restituisce se una riga giorno del Timesheet deve essere disabilitata in UI.
   *
   * Oltre ai blocchi storici (ferie, malattia, ecc.), consideriamo bloccate
   * anche le giornate part-time non lavorative.
   */
  isDayBlocked(day: any): boolean {
    return !!day?.isBlocked || (day?.is_part_time_day && !day?.is_working_day);
  }

  /**
   * Compone la label del badge part-time nell'header della settimana.
   *
   * Mostriamo i minuti attesi del singolo giorno così il badge rimane coerente
   * con la distribuzione effettiva letta dal backend.
   */
  getPartTimeBadgeLabel(expectedMinutes?: number): string {
    return 'PT allineato';
  }

  /**
   * Restituisce il tooltip del badge "da verificare".
   *
   * Per le giornate part-time lavorative mostriamo il monte ore atteso del
   * giorno, così l'utente capisce subito la soglia con cui il Timesheet sta
   * confrontando il totale inserito.
   */
  getIncorrectDayTooltip(column: DayColumn): string {
    if (!column.is_part_time_day || !column.is_working_day) {
      return 'Da verificare';
    }

    const minutes = Number(column.expected_minutes ?? 0);

    if (!minutes) {
      return 'Da verificare';
    }

    const hours = minutes / 60;
    const value = Number.isInteger(hours)
      ? `${hours}`
      : `${hours}`.replace('.', ',');

    return `Ore lavorative attese: ${value}h`;
  }

  /**
   * Replica la disponibilità storica del bottone "ignore day" per i casi non
   * part-time.
   *
   * Prima dell'introduzione dei contratti part-time, il FE abilitava l'azione
   * quando la expectedCapacity utente era diversa da 480 minuti.
   */
  private getLegacyIgnoreDayAvailability(): boolean {
    const selectedUserFromList = this.usersSelect.find(
      (user: any) => user.id === this.chosenUser
    );

    const expectedMinutes = Number(
      selectedUserFromList?.expectedCapacity?.expected_minutes ??
        this.selectedUser?.expectedCapacity?.expected_minutes ??
        480
    );

    return expectedMinutes !== 480;
  }

  /**
   * Funzione che serve per aprire la modale per la richiesta di un timesheet
   * @param day data del timesheet
   * @param request booleano che indica se è una richiesta o una modifica
   * @param note eventuale nota da visualizzare nel modal
   */
  openModalTimesheetRequest(day: string, request?: boolean, note?: any) {
    const modalRef = this._NzmodalService.create({
      nzContent: ModalRequestTimesheetComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: '654px',
      nzClassName: 'custom-modal-request',
      nzComponentParams: {
        projects: this.projects,
        dateHeader: day,
        note: note,
        totaliTimesheet: this.totaliTimesheet,
        isRequest: request,
        chosenUser: this.chosenUser,
        profileData: this.profileData
      }
    });

    const modalComponent = modalRef.getContentComponent();

    // SOTTOSCRIVITI all’evento @Output notifyParent
    modalComponent.notifyParent.subscribe(async () => {
      await this.getTimesheetUsers();
      this.getTimesheet();
      if (this.selectedUser) {
        this.selectedUser = this.usersSelect.find((user: any) => {
          return user.id === this.chosenUser;
        });
      }
    });
  }

  /**
   * Funzione che serve per cambiare l'utente selezionato
   * @param user  utente selezionato
   */
  changeUserSelected(user: any, index: number) {
    this.indexUserSelected = index;

    this.chosenUser = user.id;
    this.selectedUser = user;
    this.setHighlightedErrorDates(user.incorrect_days);
    this.setHighlightedRequestDates(user.requests);
    this.navigateToRequestWeekIfNeeded(user.requests ?? []);
    // chiamata per prendere il timesheet dell'utente selezionato
    this.getTimesheet();
  }

  private navigateToRequestWeekIfNeeded(requestDates: string[]): void {
    if (!requestDates.length || this.selectedWeekContainsAny(requestDates)) {
      return;
    }

    this.setSelectedWeekFromDate(requestDates[0]);
  }

  private selectedWeekContainsAny(dates: string[]): boolean {
    if (!this.selectedWeek?.start) {
      return false;
    }

    const weekStart = moment(this.selectedWeek.start).startOf('isoWeek');
    const weekEnd = weekStart.clone().isoWeekday(5);
    return dates.some(date =>
      moment(date).isBetween(weekStart, weekEnd, 'day', '[]')
    );
  }

  private setSelectedWeekFromDate(date: string | Date): void {
    const weekStart = moment(date).startOf('isoWeek');
    this.selectedDate = weekStart.toDate();
    this.selectedWeek = {
      start: weekStart.format('YYYY-MM-DD'),
      end: weekStart.clone().isoWeekday(5).format('YYYY-MM-DD')
    };
  }

  /**
   * Restituisce il numero di utenti che hanno giorni errati
   */
  countUsersWithIncorrectDays(): number {
    return this.usersSelect.filter(user => user.incorrect_days.length > 0)
      .length;
  }

  /**
   * Restituisce il numero di utenti che hanno delle richieste
   */
  countUsersWithRequest(): number {
    return this.usersSelect.filter(user => user.requests.length > 0).length;
  }

  /**
   * Questa funzione serve per controllare se almeno 1 utente ha una richiesta
   */
  haveRequests(): boolean {
    return this.usersSelect.some(user => user.requests.length > 0);
  }

  /**
   * Questa funzione serve per evidenziare le date dove ci sono deglie errori di un utente
   * @param dates array di date da evidenziare
   */
  setHighlightedErrorDates(dates: string[]) {
    // Filtra giorni futuri, sabati, domeniche e oggi
    this.highlightedDatesError = (dates ?? []).filter(d => {
      const day = moment(d);
      return (
        day.isBefore(this.currentDate, 'day') &&
        ![6, 7].includes(day.isoWeekday()) && // 6 sab, 7 dom
        d !== this.currentDate
      );
    });
  }

  /**
   * Questa funzione serve per controllare se una data è da evidenziare
   * @param current data corrente da controllare
   * @returns boolean
   */
  isHighlightedError(current: Date): boolean {
    const formatted = moment(current).format('YYYY-MM-DD');
    return this.highlightedDatesError?.includes(formatted);
  }

  /**
   * Questa funzione serve per evidenziare le date dove ci sono deglie errori di un utente
   * @param dates array di date da evidenziare
   */
  setHighlightedRequestDates(dates: string[]) {
    this.highlightedDatesRequest = dates;
  }

  /**
   * Questa funzione serve per controllare se una data è da evidenziare
   * @param current data corrente da controllare
   * @returns boolean
   */
  isHighlightedRequest(current: Date): boolean {
    const formatted = moment(current).format('YYYY-MM-DD');
    return this.highlightedDatesRequest?.includes(formatted);
  }

  /**
   * Questa funzione serve per diabilitare le date future nel datepicker
   * @param current data corrente
   */
  disabledFutureDates = (current: Date): boolean => {
    return moment(current).isAfter(moment(this.currentDate), 'day');
  };

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Funzione che serve per ignorare un giorno con errore
   * @param date data da ignorare
   */
  ignoreDay(date: string): void {
    if (!this.chosenUser) return;

    this._timesheetService.ignoreDay(this.chosenUser, date).subscribe({
      next: (res: any) => {
        this.responseToast('success', res.message);

        //  Aggiorna solo la data interessata in tempo reale
        this.totaliTimesheet = this.totaliTimesheet.map(day => {
          if (day.date === date) {
            return { ...day, is_ignored: true };
          }
          return day;
        });

        this.incorrectDays = this.incorrectDays.filter(d => d !== date);

        // Aggiorna anche l'elenco utenti a sinistra
        this.usersSelect = this.usersSelect.map(user => {
          if (user.id === this.chosenUser) {
            const updatedUser = {
              ...user,
              incorrect_days: user.incorrect_days.filter(
                (d: string) => d !== date
              )
            };
            this.setHighlightedErrorDates(updatedUser.incorrect_days);

            return updatedUser;
          }
          return user;
        });
      },
      error: (err: any) => {
        this.responseToast('error', err.error?.message ?? 'Errore inatteso');
      }
    });
  }
  /**   * Funzione che serve per controllare se un giorno è stato marcato come errato
   * @param date data da controllare
   * @returns boolean
   */
  isIncorrectDay(date: string): boolean {
    return this.selectedUser?.incorrect_days?.includes(date);
  }
  // Naviga avanti o indietro di una settimana
  changeWeek(direction: number): void {
    const newDate = moment(this.selectedDate).add(direction, 'week').toDate();
    this.onDateChange(newDate);
    this.selectedDate = newDate;
  }

  // (Facoltativo) Disabilita frecce se superi limiti temporali
  isNextWeekDisabled(): boolean {
    const nextWeek = moment(this.selectedDate).add(1, 'week');
    return nextWeek.isAfter(moment(), 'week'); // disabilita se > settimana corrente
  }

  isPreviousWeekDisabled(): boolean {
    const minDate = moment().subtract(6, 'months'); // es. limite a 6 mesi fa
    return moment(this.selectedDate).isBefore(minDate, 'week');
  }

  /**
   * Metodo che serve per aprire la modale delle note
   * @param note note inserite
   * @param date data del giorno
   */
  openModalNote(note: string, date: string) {
    const isUserLogged = this.chosenUser
      ? this.chosenUser == this.profileData.id
      : true;

    // creo l'oggeto user da passare alla modale
    const user = {
      firstName: this.selectedUser.first_name,
      lastName: this.selectedUser.last_name,
      path: this.selectedUser.path
    };

    // costruisco un oggetto da passare in input alla modale
    const objToSend = {
      note,
      date,
      isUserLogged,
      user
    };

    // richiamo il metodo openModal per creare la modale, e la assegno ad una costante per poi catturare l'evento passato alla chiusura
    const modalRef = this._modalService.openModal(
      ModalNoteTimesheetComponent,
      421,
      objToSend
    );

    // alla chiusura della modale catturo l'evento mandato da essa.
    modalRef.afterClose.subscribe((event: any) => {
      // nel caso salviamo le note con successo, richiamiamo la get di timesheet
      if (event == 'success') {
        this.getTimesheet();
      }
    });
  }
}
